/**
 * Trip service - Business logic for trip CRUD operations
 * Integrates with backend API for data persistence
 */

import { validateCreateTrip } from '../utils/validation';
import { formatDateForInput } from '../utils/dates';
import { apiGet, apiPost, apiPut, apiDelete } from './apiClient';
import type {
  Trip,
  CreateTripInput,
  TripListResponse,
  TripListItemDTO,
  TripDetailResponse,
  TripDetailDTO,
  CreateTripRequest,
  CreateTripResponse,
  UpdateTripRequest,
  UpdateTripResponse,
  DeleteTripResponse,
} from '../types/trip';

/**
 * Convert backend list DTO to frontend Trip entity
 */
function convertDTOToTrip(dto: TripListItemDTO): Trip {
  // 處理 Snowflake ID：確保 tripId 保持為字串，避免精度丟失
  const tripId = typeof dto.tripId === 'string' ? dto.tripId : String(dto.tripId);

  return {
    id: tripId,
    name: dto.name,
    startDate: new Date(dto.startDate),
    endDate: new Date(dto.endDate),
    userId: String(dto.userId),
    tags: [], // Backend doesn't return tags in list API
    createdAt: new Date(), // Backend doesn't return timestamps in list API
    updatedAt: new Date(),
  };
}

/**
 * Convert backend detail DTO to frontend Trip entity
 */
function convertDetailDTOToTrip(dto: TripDetailDTO): Trip {
  // 處理 Snowflake ID：確保 tripId 保持為字串，避免精度丟失
  const tripId = typeof dto.tripId === 'string' ? dto.tripId : String(dto.tripId);

  return {
    id: tripId,
    name: dto.name,
    description: dto.description,
    destination: dto.destination,
    travelerCount: dto.travelerCount,
    startDate: new Date(dto.startDate),
    endDate: new Date(dto.endDate),
    userId: String(dto.userId),
    tags: [], // Backend doesn't have tags
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Get all trips for the current user from backend API
 */
export async function getAll(): Promise<Trip[]> {
  try {
    const response = await apiGet<TripListResponse>('/trip');

    if (response.exitCode !== 1) {
      throw new Error(response.message || '取得旅程列表失敗');
    }

    // Convert DTOs to Trip entities
    return response.data.map(convertDTOToTrip);
  } catch (error) {
    console.error('Failed to fetch trips:', error);
    throw new Error('無法載入旅程列表，請稍後再試');
  }
}

/**
 * Get a single trip by ID from backend API
 * Backend automatically checks if trip belongs to current user
 */
export async function getById(id: string): Promise<Trip | null> {
  try {
    const response = await apiGet<TripDetailResponse>(`/trip/${id}`);

    if (response.exitCode !== 1) {
      throw new Error(response.message || '取得旅程失敗');
    }

    return convertDetailDTOToTrip(response.data);
  } catch (error) {
    console.error('Failed to fetch trip:', error);
    return null;
  }
}

/**
 * Create a new trip via backend API
 * Automatically associates with current authenticated user (FR-016)
 */
export async function create(input: CreateTripInput): Promise<Trip> {
  try {
    // Validate input
    const validatedInput = validateCreateTrip(input);

    // Prepare request payload
    const requestData: CreateTripRequest = {
      name: validatedInput.name,
      startDate: formatDateForInput(validatedInput.startDate),
      endDate: formatDateForInput(validatedInput.endDate),
    };

    // Call backend API
    const response = await apiPost<CreateTripResponse>('/trip', requestData);

    if (response.exitCode !== 1) {
      throw new Error(response.message || '創建旅程失敗');
    }

    // Convert response to Trip entity
    const tripId = typeof response.data.tripId === 'string'
      ? response.data.tripId
      : String(response.data.tripId);

    const trip: Trip = {
      id: tripId,
      name: response.data.name,
      startDate: new Date(response.data.startDate),
      endDate: new Date(response.data.endDate),
      userId: String(response.data.userId),
      tags: validatedInput.tags || [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    return trip;
  } catch (error) {
    console.error('Failed to create trip:', error);
    throw new Error('無法創建旅程，請稍後再試');
  }
}

/**
 * Update an existing trip via backend API (PUT /trip/{tripId}).
 * This is a full replace, not a partial patch — callers must supply the
 * complete name/startDate/endDate (TripContext.updateTrip merges the
 * caller's partial UpdateTripInput with the currently-known trip before
 * calling this, so an edit that only changes the name doesn't wipe out
 * the dates).
 */
export async function update(
  id: string,
  input: {
    name: string;
    description?: string;
    destination?: string;
    travelerCount?: number;
    startDate: Date;
    endDate: Date;
  }
): Promise<Trip> {
  try {
    const requestData: UpdateTripRequest = {
      name: input.name,
      description: input.description,
      destination: input.destination,
      travelerCount: input.travelerCount,
      startDate: formatDateForInput(input.startDate),
      endDate: formatDateForInput(input.endDate),
    };

    const response = await apiPut<UpdateTripResponse>(`/trip/${id}`, requestData);

    if (response.exitCode !== 1 || !response.data) {
      throw new Error(response.message || '更新旅程失敗');
    }

    const tripId = typeof response.data.tripId === 'string' ? response.data.tripId : String(response.data.tripId);

    return {
      id: tripId,
      name: response.data.name,
      description: response.data.description,
      destination: response.data.destination,
      travelerCount: response.data.travelerCount,
      startDate: new Date(response.data.startDate),
      endDate: new Date(response.data.endDate),
      userId: String(response.data.userId),
      tags: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  } catch (error) {
    console.error('Failed to update trip:', error);
    throw new Error('無法更新旅程，請稍後再試');
  }
}

/**
 * Delete a trip via backend API (DELETE /trip/{tripId}). Owner only —
 * backend rejects with TRIP_ACCESS_DENIED otherwise. Members/itineraries/
 * polls/invite links are cascade-deleted server-side.
 */
export async function deleteTrip(id: string): Promise<void> {
  try {
    const response = await apiDelete<DeleteTripResponse>(`/trip/${id}`);

    if (response.exitCode !== 1) {
      throw new Error(response.message || '刪除旅程失敗');
    }
  } catch (error) {
    console.error('Failed to delete trip:', error);
    throw new Error('無法刪除旅程，請稍後再試');
  }
}

export const tripService = {
  getAll,
  getById,
  create,
  update,
  delete: deleteTrip,
};
